// #1028 — admin Reminders & callbacks sub-page.
//
// One-stop admin view + management of a user's reminders WITHOUT logging
// into the family Portal. Crucially it shows ALL reminders, including
// source='user' (voice-set) rows that the family Portal deliberately
// hides (#786 — the Portal surfaces what the family CONFIGURES, not what
// the user SAYS). Admin is the correct surface for conversation-derived
// data (#786/#828, same call as the Memory panel).
//
// Also surfaces #1024 user-requested callbacks (pending_calls with
// trigger_type='callback'), which are otherwise invisible in any UI —
// useful for "did the callback register / will she ring?" debugging.
//
// Backed by:
//   GET    /api/admin/users/:id/reminders            -> {reminders, callbacks}
//   PATCH  /api/admin/users/:id/reminders/:rid       -> full edit
//   DELETE /api/admin/users/:id/reminders/:rid       -> delete (+ children)
//   DELETE /api/admin/users/:id/callbacks/:cid       -> cancel callback
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';
import { userSubPageStyles, adminFetch, fmtDate, fmtDateTime } from './userSubPageStyles';

const VERSION = 'v1.0.0';

const STATUS_OPTIONS = ['pending', 'done', 'dismissed', 'delivery_failed'];
const TYPE_OPTIONS = ['normal', 'important'];
const DELIVERY_OPTIONS = ['call', 'conversation'];
const RECURRENCE_OPTIONS = ['none', 'once', 'daily', 'weekly', 'interval', 'yearly'];

function badge(text, bg, fg) {
  return (
    <span style={{
      background: bg, color: fg, padding: '2px 8px', borderRadius: 10,
      fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap',
    }}>{text}</span>
  );
}

function typeBadge(t) {
  return t === 'important'
    ? badge('important', '#FDECEA', '#B71C1C')
    : badge('normal', '#EEEEEE', '#666');
}

function deliveryBadge(m) {
  return (m || 'call') === 'conversation'
    ? badge('💬 conversation', '#E8F5E9', '#1B5E20')
    : badge('📞 call', '#E3F2FD', '#1565C0');
}

function statusBadge(s) {
  const palette = {
    pending: ['#FFF3E0', '#9c6f1a'],
    done: ['#E8F5E9', '#1B5E20'],
    dismissed: ['#EEEEEE', '#666'],
    delivery_failed: ['#FDECEA', '#B71C1C'],
    answered: ['#E8F5E9', '#1B5E20'],
    missed: ['#FDECEA', '#B71C1C'],
  };
  const [bg, fg] = palette[s] || ['#EEE', '#666'];
  return badge(s || '—', bg, fg);
}

function formatSchedule(r) {
  const bits = [];
  if (r.recurrence && r.recurrence !== 'none' && r.recurrence !== 'once') bits.push(r.recurrence);
  if (r.recur_date) bits.push(`on ${r.recur_date}`);
  if (r.due_date) bits.push(fmtDate(r.due_date));
  if (r.scheduled_time) bits.push(`at ${String(r.scheduled_time).slice(0, 5)}`);
  return bits.length ? bits.join(' ') : '—';
}

export default function AdminUserReminders() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await adminApi.getUser(id);
        if (cancelled) return;
        setUser(u);
        const r = await adminFetch(`/api/admin/users/${id}/reminders`);
        if (cancelled) return;
        setReminders(Array.isArray(r?.reminders) ? r.reminders : []);
        setCallbacks(Array.isArray(r?.callbacks) ? r.callbacks : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  function startEdit(r) {
    setEditingId(r.id);
    setForm({
      task: r.task || '',
      status: r.status || 'pending',
      reminder_type: r.reminder_type || 'normal',
      delivery_mode: r.delivery_mode || 'call',
      due_date: r.due_date || '',
      scheduled_time: r.scheduled_time ? String(r.scheduled_time).slice(0, 5) : '',
      recurrence: r.recurrence || 'none',
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm({});
  }

  async function saveEdit(rid) {
    try {
      const body = {
        task: form.task,
        status: form.status,
        reminder_type: form.reminder_type,
        delivery_mode: form.delivery_mode,
        recurrence: form.recurrence,
        // empty string -> null (clear the field)
        due_date: form.due_date || null,
        scheduled_time: form.scheduled_time || null,
      };
      await adminFetch(`/api/admin/users/${id}/reminders/${rid}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      // Optimistic local update.
      setReminders(prev => prev.map(r => r.id === rid ? { ...r, ...body } : r));
      cancelEdit();
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
  }

  async function deleteReminder(rid) {
    if (!window.confirm('Delete this reminder? This also removes any linked pending calls and alerts.')) return;
    try {
      await adminFetch(`/api/admin/users/${id}/reminders/${rid}`, { method: 'DELETE' });
      setReminders(prev => prev.filter(r => r.id !== rid));
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  }

  async function cancelCallback(cid) {
    if (!window.confirm('Cancel this upcoming callback? The tablet will not ring for it.')) return;
    try {
      await adminFetch(`/api/admin/users/${id}/callbacks/${cid}`, { method: 'DELETE' });
      setCallbacks(prev => prev.filter(c => c.id !== cid));
    } catch (e) {
      alert('Cancel failed: ' + e.message);
    }
  }

  const td = { padding: '6px 8px', verticalAlign: 'top' };
  const inputStyle = { width: '100%', boxSizing: 'border-box', fontSize: '0.85rem', padding: '3px 5px' };

  return (
    <AdminLayout title="Reminders">
      <style>{userSubPageStyles}</style>
      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <div className="page-header">
          <div className="page-title">
            {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
          </div>
          <div className="page-subtitle">
            {user.email} · Reminders &amp; callbacks (all sources, incl. voice-set)
          </div>
        </div>
      )}

      {/* ── Reminders ──────────────────────────────────────────────── */}
      <div className="section-title">
        Reminders
        <span className="section-sub">
          reminders table · ALL sources incl. <strong>source='user'</strong> (voice-set, hidden from the family Portal per #786) · edit / 🗑 delete a row
        </span>
      </div>
      {!loading && reminders.length === 0 && (
        <div className="empty-state">No reminders for this user.</div>
      )}
      {reminders.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>Task</th><th>Source</th><th>Type</th><th>Delivery</th>
                <th>Status</th><th>Schedule</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {reminders.map(r => editingId === r.id ? (
                <tr key={r.id} style={{ background: '#FAFBFF' }}>
                  <td style={td}>
                    <input style={inputStyle} value={form.task}
                      onChange={e => setForm(f => ({ ...f, task: e.target.value }))} />
                  </td>
                  <td style={td}>{r.source}</td>
                  <td style={td}>
                    <select value={form.reminder_type}
                      onChange={e => setForm(f => ({ ...f, reminder_type: e.target.value }))}>
                      {TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <select value={form.delivery_mode}
                      onChange={e => setForm(f => ({ ...f, delivery_mode: e.target.value }))}>
                      {DELIVERY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <select value={form.status}
                      onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                      {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <select value={form.recurrence}
                        onChange={e => setForm(f => ({ ...f, recurrence: e.target.value }))}>
                        {RECURRENCE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <input type="date" style={inputStyle} value={form.due_date || ''}
                        onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
                      <input type="time" style={inputStyle} value={form.scheduled_time || ''}
                        onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))} />
                    </div>
                  </td>
                  <td style={td}>{fmtDate(r.created_at)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => saveEdit(r.id)}
                      style={{ marginRight: 6, cursor: 'pointer' }}>Save</button>
                    <button onClick={cancelEdit} style={{ cursor: 'pointer' }}>Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={r.id}>
                  <td style={td}>{r.task}</td>
                  <td style={td}>{r.source}</td>
                  <td style={td}>{typeBadge(r.reminder_type)}</td>
                  <td style={td}>{deliveryBadge(r.delivery_mode)}</td>
                  <td style={td}>{statusBadge(r.status)}</td>
                  <td style={td}>{formatSchedule(r)}</td>
                  <td style={{ ...td, color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {fmtDate(r.created_at)}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => startEdit(r)}
                      style={{ background: 'none', border: 'none', color: '#1565C0', cursor: 'pointer', padding: '2px 6px' }}
                      title="Edit reminder">✏️</button>
                    <button onClick={() => deleteReminder(r.id)}
                      style={{ background: 'none', border: 'none', color: '#DC3545', cursor: 'pointer', padding: '2px 6px' }}
                      title="Delete reminder">🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Callbacks (#1024) ──────────────────────────────────────── */}
      <div className="section-title" style={{ marginTop: 32 }}>
        Callbacks (user-requested)
        <span className="section-sub">
          pending_calls · trigger_type='callback' (#1024) · "call me back in N minutes" · cancel an upcoming one before it rings
        </span>
      </div>
      {!loading && callbacks.length === 0 && (
        <div className="empty-state">No callbacks for this user.</div>
      )}
      {callbacks.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>Scheduled for</th><th>Status</th><th>Context</th><th>Requested</th><th></th>
              </tr>
            </thead>
            <tbody>
              {callbacks.map(c => (
                <tr key={c.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(c.scheduled_at)}</td>
                  <td style={td}>{statusBadge(c.status)}</td>
                  <td style={td}>{c.context || <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td style={{ ...td, color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                    {fmtDateTime(c.created_at)}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {c.status === 'pending' ? (
                      <button onClick={() => cancelCallback(c.id)}
                        style={{ background: 'none', border: 'none', color: '#DC3545', cursor: 'pointer', padding: '2px 6px' }}
                        title="Cancel callback">🗑 cancel</button>
                    ) : <span style={{ color: '#ccc' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
